/**
 * AudioCache — tier-aware IndexedDB audio cache implementation.
 *
 * See AudioCache.types.ts for the contract. This implementation backs
 * both namespaces with a single IndexedDB store (`ssi-audio-cache-v2`),
 * keyed by audio id, with a `lifecycle` column distinguishing the two
 * namespaces. In-memory id Sets make `has(id)` synchronous and cheap.
 */

import { openDB, deleteDB, type IDBPDatabase, type DBSchema } from 'idb'
import type {
  AudioCache,
  AudioCacheStats,
  AudioId,
  CreateAudioCacheOptions,
  EphemeralLegoSet,
  EphemeralNamespace,
  PersistentNamespace,
} from './AudioCache.types'
import type { AudioLifecycle } from '../types/courseBundle'
import { bytesToWavBlob } from './wav'
import { estimateStorage, storagePressure } from '../platform/storage'
import { apiUrl } from '../platform/apiBase'

const DB_NAME = 'ssi-audio-cache-v2'
const DB_VERSION = 1
const STORE = 'audio'
const STATS_TTL_MS = 5000
// Keys per page of the id walk: no single IndexedDB request ever covers the
// whole store (job #828).
export const WALK_BATCH = 500

// Ceiling on the offline WAV read (IndexedDB fetch + mp3→WAV decode).
//
// Same disease as the fetch timeout in doFetchAndStore, one layer up: this
// promise is MEMOISED per audio id in `wavUrlInflight`, so if it never settles
// every future request for that id gets handed the same dead promise. The
// playback path awaits it before assigning a src, which is how one clip took a
// whole session down (Aran, 2026-08-06, German "with you") and why replaying
// the item re-entered the stall while every other item played fine.
// ctx.decodeAudioData is the unbounded primitive underneath — WebKit will not
// always settle it (an interrupted audio session, a payload it chokes on).
// Past the ceiling we resolve null and the caller falls back to the network
// URL, exactly as it does for a plain cache miss.
const WAV_READ_TIMEOUT_MS = 5000

// How many warmed clips' bytes adoptBytes() holds in memory (job #298). The
// warm-ahead only ever runs a cycle or two in front of the playhead, so a
// couple of dozen mp3s (~10-70 KB each) covers it; past this the oldest go.
const ADOPTED_BYTES_MAX = 24

// Shared with audioCacheOwner.ts: the localStorage key naming the learner whose
// audio is cached. A change of it in another tab means the store was emptied.
export const OWNER_KEY = 'ssi-audio-cache-owner'

// An open slower than this is reported (job #168): a wedged IndexedDB used to
// be invisible, every write silently lost and every play streamed.
const SLOW_OPEN_MS = 3000

// Set (to a timestamp) when an identity-change purge could not empty the store:
// every row cached at or before it belongs to the previous learner and is
// treated as absent until the per-record sweep has deleted it (job #202).
export const STALE_KEY = 'ssi-audio-cache-stale-before'

export interface AudioCacheTrouble {
  stage: 'open-slow' | 'put'
  /** Which write path failed, for stage 'put'. */
  where?: 'ensure' | 'promote' | 'touch'
  /** Stage 'open-slow': the open had not resolved when reported. */
  pending?: boolean
  ms?: number
  name?: string
  message?: string
}

/** Window event the player turns into an `audio_cache_trouble` telemetry row. */
export const AUDIO_CACHE_TROUBLE_EVENT = 'ssi-audio-cache-trouble'

function readStaleBefore(): number {
  try { return Number(localStorage.getItem(STALE_KEY)) || 0 } catch { return 0 }
}

function reportTrouble(detail: AudioCacheTrouble): void {
  console.warn('[AudioCache] trouble', detail)
  if (typeof window === 'undefined') return
  try { window.dispatchEvent(new CustomEvent(AUDIO_CACHE_TROUBLE_EVENT, { detail })) } catch { /* no CustomEvent */ }
}

interface AudioRow {
  id: string
  blob: Blob
  mimeType: string
  size: number
  lifecycle: AudioLifecycle
  // Vestigial: always written null. The per-course clear API that consumed it
  // was removed (it had no production caller and the write path never
  // populated this, so it could only ever no-op). Kept in the row + the
  // `by-course` index to avoid a DB_VERSION migration; repurpose or drop both
  // in the next schema bump if still unused.
  courseCode: string | null
  cachedAt: number
  lastAccessedAt: number
  ephemeralOwnerLegoId: string | null
}

interface CacheSchema extends DBSchema {
  audio: {
    key: string
    value: AudioRow
    indexes: {
      'by-lifecycle': AudioLifecycle
      'by-course': string
      'by-last-accessed': number
      'by-ephemeral-owner': string
    }
  }
}

/**
 * An id Set that reports every mutation, so the background walk can tell which
 * ids changed after its snapshot was taken and leave those alone (job #804).
 */
class TrackedIdSet extends Set<string> {
  onTouch: ((id: string | null) => void) | null = null
  override add(id: string): this { this.onTouch?.(id); return super.add(id) }
  override delete(id: string): boolean { this.onTouch?.(id); return super.delete(id) }
  override clear(): void { this.onTouch?.(null); super.clear() }
}

export class AudioCacheImpl implements AudioCache {
  private db: IDBPDatabase<CacheSchema> | null = null
  private openPromise: Promise<void> | null = null
  private hydratePromise: Promise<void> | null = null

  private readonly persistentIds = new TrackedIdSet()
  private readonly ephemeralIds = new TrackedIdSet()

  // Offline WAV blob URLs, keyed by audio id — decoded once and reused for
  // repeated clips (spaced rep replays the same audio). See getWavBlobUrl.
  private readonly wavUrlCache: Map<string, string> = new Map()

  // In-flight WAV decodes, keyed by id. Concurrent getWavBlobUrl calls for the
  // same id (prefetch racing playback) share ONE decode + ONE object URL, so a
  // late caller can't overwrite the map with a second URL without revoking the
  // first (the blob-URL leak) and the mp3→WAV re-encode runs once, not twice.
  private readonly wavUrlInflight: Map<string, Promise<string | null>> = new Map()

  // Clip bytes a warm-up has already pulled off the network (adoptBytes), by
  // id. Read by getWavBlobUrl in place of IndexedDB and by ensure in place of
  // a second download. Insertion-ordered, capped at ADOPTED_BYTES_MAX.
  private readonly adoptedBytes: Map<string, { bytes: ArrayBuffer; type: string }> = new Map()

  /** In-flight ensure/acquire de-dupe: one Promise per id. */
  private readonly inflight: Map<string, Promise<void>> = new Map()

  /** Per-lego abort controllers — release aborts in-flight acquires. */
  private readonly legoAborts: Map<string, AbortController> = new Map()

  /** stats() cache to avoid hammering IndexedDB. */
  private statsCache: { at: number; value: AudioCacheStats } | null = null

  private readonly audioUrl: (id: AudioId) => string
  private readonly fetchTimeoutMs: number

  readonly persistent: PersistentNamespace
  readonly ephemeral: EphemeralNamespace

  constructor(options: CreateAudioCacheOptions = {}) {
    this.audioUrl = options.audioUrl ?? ((id) => apiUrl(`/api/audio/${id}`))
    this.fetchTimeoutMs = options.fetchTimeoutMs ?? 30_000

    // Bind namespace facades to `this`.
    this.persistent = {
      ensure: (id) => this.persistentEnsure(id),
      ensureFromUrl: (id, url) => this.persistentEnsureFromUrl(id, url),
      has: (id) => this.persistentIds.has(id),
      getBlobUrl: (id) => this.getBlobUrl(id),
      evictToTarget: (targetBytes) => this.persistentEvictToTarget(targetBytes),
    }
    this.ephemeral = {
      acquireForLego: (set) => this.ephemeralAcquireForLego(set),
      releaseForLego: (legoId) => this.ephemeralReleaseForLego(legoId),
      has: (id) => this.ephemeralIds.has(id),
      getBlobUrl: (id) => this.getBlobUrl(id),
    }
    // Another tab purged the store for a new learner (audioCacheOwner.ts):
    // what this tab remembers being cached is no longer there.
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', (e) => {
        if (e.key === OWNER_KEY) this.forgetInMemory()
        if (e.key === STALE_KEY) { this.staleBefore = readStaleBefore(); this.forgetInMemory() }
      })
    }
  }

  /** Rows cached at or before this are the previous learner's: invisible. */
  private staleBefore = readStaleBefore()
  private sweeping: Promise<void> | null = null

  private isStaleRow(row: { cachedAt?: number } | undefined): boolean {
    return !!row && this.staleBefore > 0 && !((row.cachedAt ?? 0) > this.staleBefore)
  }

  /**
   * The purge's fallback when the store could not be emptied (job #202). Never
   * deletes the database — a blocked delete queues every open on the origin
   * behind it. Marks everything cached so far as the previous learner's
   * (invisible to every read), then removes those rows record by record, each
   * in its own short readwrite transaction that no other connection can block.
   */
  markStale(): void {
    this.staleBefore = Date.now()
    try { localStorage.setItem(STALE_KEY, String(this.staleBefore)) } catch { /* in-memory only */ }
    this.forgetInMemory()
    void this.sweepStale()
  }

  /** Delete stale rows one by one; lift the marker only when none are left. */
  sweepStale(): Promise<void> {
    if (!this.staleBefore) return Promise.resolve()
    if (!this.sweeping) {
      const cutoff = this.staleBefore
      this.sweeping = (async () => {
        await this.open()
        if (!this.db) return
        const keys = await this.db.getAllKeys(STORE)
        for (const key of keys) {
          // Read, judge and delete in ONE readwrite transaction: a fresh row
          // another tab writes between a separate get and delete must survive.
          const tx = this.db.transaction(STORE, 'readwrite')
          const row = await tx.store.get(key)
          if (row && !((row.cachedAt ?? 0) > cutoff)) await tx.store.delete(key)
          await tx.done
        }
        if (this.staleBefore === cutoff) {
          this.staleBefore = 0
          try { localStorage.removeItem(STALE_KEY) } catch { /* nothing to do */ }
        }
      })().catch((err) => {
        console.warn('[AudioCache] stale sweep failed; will retry next open', err)
      }).finally(() => { this.sweeping = null })
    }
    return this.sweeping
  }

  /** Close the connection and forget what it told us; the next call re-opens. */
  private releaseConnection(): void {
    try { this.db?.close() } catch { /* already closed */ }
    this.db = null
    this.openPromise = null
    this.forgetInMemory()
  }

  /** Drop every in-memory answer about the store; the walk re-runs on ready(). */
  private forgetInMemory(): void {
    this.hydratePromise = null
    this.persistentIds.clear()
    this.ephemeralIds.clear()
    for (const url of this.wavUrlCache.values()) URL.revokeObjectURL(url)
    this.wavUrlCache.clear()
    // Held boot bytes are as much the last learner's as any stored clip: the purge takes them too.
    this.adoptedBytes.clear()
    this.statsCache = null
  }

  /**
   * Empty the store in place. The identity-change purge uses this instead of
   * deleting the database: a delete is blocked by any other tab holding a
   * connection, and a clear never is (job #168).
   */
  async clearAll(): Promise<void> {
    await this.open()
    if (!this.db) return
    await this.db.clear(STORE)
    this.forgetInMemory()
  }

  // ==========================================================================
  // INIT
  // ==========================================================================

  /**
   * Await the in-memory id Sets being populated from IndexedDB.
   *
   * `persistent.has(id)` is a SYNCHRONOUS read of `persistentIds`, and that Set
   * is filled only by doInit()'s cursor walk — which runs lazily, off whichever
   * async cache method happens to be called first. Until it finishes, has()
   * answers FALSE FOR EVERYTHING.
   *
   * Offline that is load-bearing: every cycle with a real audio url looks
   * uncached and gets skipped, so the only cycles that survive the gate are the
   * ones with no audio at all. The race doesn't create the silence, it
   * concentrates it at the start of a session — which is exactly where Tom hit
   * it, 2026-08-15. Callers that are about to trust has() must await this first.
   */
  async ready(): Promise<void> {
    await this.open()
    await this.hydrate()
  }

  /**
   * Every cache operation needs the database OPEN, which is quick; none of them
   * needs the id walk, which on a phone holding several downloaded courses took
   * 80-140 s (job #804). So init() only opens. The walk starts when somebody
   * calls ready(), and the player does that after the first sound (job #828:
   * a walk merely STARTED at boot still held Tom's first script read for 135 s,
   * because WebKit runs one origin's IndexedDB requests one after another).
   */
  private async init(): Promise<void> {
    await this.open()
  }

  private open(): Promise<void> {
    if (!this.openPromise) {
      this.openPromise = this.doOpen().catch((err) => { this.openPromise = null; throw err })
    }
    return this.openPromise
  }

  private hydrate(): Promise<void> {
    if (!this.hydratePromise) {
      this.hydratePromise = this.doHydrate().catch((err) => { this.hydratePromise = null; throw err })
    }
    return this.hydratePromise
  }

  /**
   * Answer has() for exactly these ids now, by primary-key lookups, without
   * waiting for the whole walk. For the clips about to play when the offline
   * gate is about to trust has() (job #804).
   */
  async primeIds(ids: Iterable<string>): Promise<void> {
    await this.open()
    if (!this.db) return
    const wanted = [...new Set(ids)].filter((id) => id && !this.persistentIds.has(id) && !this.ephemeralIds.has(id))
    if (!wanted.length) return
    const tx = this.db.transaction(STORE, 'readonly')
    const store = tx.objectStore(STORE)
    const rows = await Promise.all(wanted.map((id) => store.get(id)))
    await tx.done
    for (const row of rows) {
      if (!row || this.isStaleRow(row)) continue
      // Only if nothing changed this id meanwhile: a write or delete that
      // landed while we read is newer than what we read.
      if (this.persistentIds.has(row.id) || this.ephemeralIds.has(row.id)) continue
      if (row.lifecycle === 'persistent') this.persistentIds.add(row.id)
      else this.ephemeralIds.add(row.id)
    }
  }

  private async doOpen(): Promise<void> {
    const release = () => this.releaseConnection()
    const open = () => openDB<CacheSchema>(DB_NAME, DB_VERSION, {
      // ANOTHER TAB WANTS THE DATABASE GONE OR UPGRADED: let go at once (job
      // #168). Holding on blocks its deleteDatabase, and every open on the
      // origin, this tab's included, then queues behind that delete forever:
      // no clip is ever stored, has() is false for everything, and every read
      // waits out its timeout before streaming. Tom's Safari, 2026-10-01:
      // 0 cache hits in 160 plays. The next cache call re-opens.
      blocking: release,
      // WebKit's IndexedDB server can drop a connection; re-open next call.
      terminated: release,
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          const store = db.createObjectStore(STORE, { keyPath: 'id' })
          store.createIndex('by-lifecycle', 'lifecycle')
          store.createIndex('by-course', 'courseCode')
          store.createIndex('by-last-accessed', 'lastAccessedAt')
          store.createIndex('by-ephemeral-owner', 'ephemeralOwnerLegoId')
        }
      },
    })
    const t0 = Date.now()
    // An open that never resolves must report too: the timer fires from the
    // start, not after the await (job #202).
    let reported = false
    const slowTimer = setTimeout(() => {
      reported = true
      reportTrouble({ stage: 'open-slow', ms: SLOW_OPEN_MS, pending: true })
    }, SLOW_OPEN_MS)
    let db: IDBPDatabase<CacheSchema>
    try {
      db = await open()
    } finally {
      clearTimeout(slowTimer)
    }
    const openMs = Date.now() - t0
    if (openMs > SLOW_OPEN_MS && !reported) reportTrouble({ stage: 'open-slow', ms: openMs })
    // Self-heal a storeless DB (interrupted upgrade / a ?reset that opened it
    // without recreating stores) — otherwise the very next transaction throws
    // NotFoundError and the whole audio cache is dead. Delete and recreate.
    if (!db.objectStoreNames.contains(STORE)) {
      db.close()
      await deleteDB(DB_NAME)
      db = await open()
    }
    this.db = db
    if (this.staleBefore) void this.sweepStale()
  }

  private async doHydrate(): Promise<void> {
    if (!this.db) return
    // Pending purge: what is on disk is the previous learner's, so the walk
    // must not make it look cached. Fresh writes add their own ids.
    if (this.staleBefore) return
    const touched = new Set<string | null>()
    const onTouch = (id: string | null) => { touched.add(id) }
    this.persistentIds.onTouch = onTouch
    this.ephemeralIds.onTouch = onTouch
    try {
      await this.walk(touched)
    } finally {
      this.persistentIds.onTouch = null
      this.ephemeralIds.onTouch = null
    }
  }

  private async walk(touched: Set<string | null>): Promise<void> {
    if (!this.db) return
    // KEYS ONLY: a value cursor deserialises every row, and every row carries
    // its audio Blob (job #804). And NEVER ONE REQUEST FOR THE WHOLE STORE: a
    // single getAllKeys over Tom's six downloaded courses took 80-140 s on his
    // iPhone, and WebKit serves an origin's IndexedDB requests one after
    // another, so every other read on the device, the script cache's included,
    // queued behind it (job #828). The walk goes in pages of WALK_BATCH keys,
    // each its own short transaction, and yields between pages.
    //
    // Ephemeral rows are the small working set of the next few LEGOs, read
    // one key at a time off the lifecycle index; every other key is persistent.
    const ephemeral = new Set<string>()
    {
      const tx = this.db.transaction(STORE, 'readonly')
      let cursor = await tx.store.index('by-lifecycle').openKeyCursor(IDBKeyRange.only('ephemeral'))
      while (cursor) {
        ephemeral.add(cursor.primaryKey)
        cursor = await cursor.continue()
      }
      await tx.done
    }
    // The walk's snapshot is older than any mutation made while it ran: skip
    // those ids (and everything, if the Sets were cleared meanwhile).
    const addRaw = (set: Set<string>, id: string) => { if (!touched.has(id)) Set.prototype.add.call(set, id) }
    if (touched.has(null)) return
    for (const id of ephemeral) addRaw(this.ephemeralIds, id)
    let after: IDBKeyRange | undefined
    for (;;) {
      const keys = await this.db.getAllKeys(STORE, after, WALK_BATCH)
      if (touched.has(null)) return
      for (const id of keys) if (!ephemeral.has(id)) addRaw(this.persistentIds, id)
      if (keys.length < WALK_BATCH) return
      after = IDBKeyRange.lowerBound(keys[keys.length - 1], true)
      await new Promise((resolve) => setTimeout(resolve, 0))
    }
  }

  // ==========================================================================
  // PERSISTENT
  // ==========================================================================

  private persistentEnsure(id: AudioId): Promise<void> {
    if (this.persistentIds.has(id)) return Promise.resolve()
    const existing = this.inflight.get(id)
    if (existing) return existing

    const p = this.doFetchAndStore(id, {
      lifecycle: 'persistent',
      ephemeralOwnerLegoId: null,
    })
      .finally(() => {
        this.inflight.delete(id)
      })
    this.inflight.set(id, p)
    return p
  }

  private persistentEnsureFromUrl(id: AudioId, url: string): Promise<void> {
    if (this.persistentIds.has(id)) return Promise.resolve()
    const existing = this.inflight.get(id)
    if (existing) return existing

    const p = this.doFetchAndStore(id, {
      lifecycle: 'persistent',
      ephemeralOwnerLegoId: null,
      url,
    })
      .finally(() => {
        this.inflight.delete(id)
      })
    this.inflight.set(id, p)
    return p
  }

  private async doFetchAndStore(
    id: AudioId,
    opts: { lifecycle: AudioLifecycle; ephemeralOwnerLegoId: string | null; signal?: AbortSignal; url?: string },
  ): Promise<void> {
    await this.init()
    if (!this.db) throw new Error('AudioCache: DB not initialized')

    // The id walk may not have run yet (it waits for the first sound, job
    // #828): ask the store for this one id, so a clip already on the device
    // is never fetched again.
    if (!this.persistentIds.has(id) && !this.ephemeralIds.has(id)) {
      const row = await this.db.get(STORE, id)
      if (row && !this.isStaleRow(row)) (row.lifecycle === 'persistent' ? this.persistentIds : this.ephemeralIds).add(id)
    }

    // If already cached in either namespace, persistent wins — skip duplicate.
    if (this.persistentIds.has(id)) return
    if (opts.lifecycle === 'ephemeral' && this.ephemeralIds.has(id)) return

    // Cross-namespace promotion: id is already cached as ephemeral and the
    // caller wants it persistent. Rewrite the existing row's lifecycle in
    // place instead of re-fetching, and move it between the in-memory Sets.
    if (opts.lifecycle === 'persistent' && this.ephemeralIds.has(id)) {
      const existing = await this.db.get(STORE, id)
      if (existing && !this.isStaleRow(existing)) {
        const promoted: AudioRow = {
          ...existing,
          lifecycle: 'persistent',
          ephemeralOwnerLegoId: null,
          lastAccessedAt: Date.now(),
        }
        try {
          await this.db.put(STORE, promoted)
        } catch (err) {
          reportTrouble({ stage: 'put', where: 'promote', name: (err as Error)?.name, message: (err as Error)?.message })
          throw err
        }
        this.ephemeralIds.delete(id)
        this.persistentIds.add(id)
        this.statsCache = null
        return
      }
      // Row missing from DB despite Set membership — fall through to fetch
      // and self-heal the inconsistency.
      this.ephemeralIds.delete(id)
    }

    // Hard per-fetch timeout covering headers AND body. A hung connection
    // otherwise never settles this promise, and since it lives in the
    // in-flight de-dupe map every retry of the id gets the same dead promise
    // — the silent bulk-download freeze (founder stall, 2026-07-31). The
    // timeout converts the hang into a rejection the callers' retry paths
    // already handle, and `.finally` clears the in-flight entry so the next
    // attempt fetches for real.
    const controller = new AbortController()
    const timer = setTimeout(
      () => controller.abort(new DOMException(`AudioCache: fetch ${id} timed out`, 'TimeoutError')),
      this.fetchTimeoutMs,
    )
    const onOuterAbort = () => controller.abort(new DOMException('Aborted', 'AbortError'))
    if (opts.signal?.aborted) onOuterAbort()
    else opts.signal?.addEventListener('abort', onOuterAbort, { once: true })

    let blob: Blob
    try {
      const adopted = this.adoptedBytes.get(id)
      if (adopted) {
        // A warm-up already downloaded this clip: store those bytes rather
        // than fetching the same mp3 a second time (job #298).
        blob = new Blob([adopted.bytes], { type: adopted.type })
      } else {
        const res = await fetch(opts.url ?? this.audioUrl(id), { signal: controller.signal })
        if (!res.ok) {
          throw new Error(`AudioCache: fetch ${id} → ${res.status}`)
        }
        blob = await res.blob()
      }
    } finally {
      clearTimeout(timer)
      opts.signal?.removeEventListener('abort', onOuterAbort)
    }

    if (opts.signal?.aborted) {
      // Caller already abandoned us; don't persist.
      throw new DOMException('Aborted', 'AbortError')
    }

    const now = Date.now()
    const row: AudioRow = {
      id,
      blob,
      mimeType: blob.type || 'audio/mpeg',
      size: blob.size,
      lifecycle: opts.lifecycle,
      courseCode: null,
      cachedAt: now,
      lastAccessedAt: now,
      ephemeralOwnerLegoId: opts.ephemeralOwnerLegoId,
    }
    try {
      // The connection can be released while the fetch above is in flight (a
      // purge, an identity change, versionchange); open() re-opens it, so the
      // clip is stored instead of throwing on a null db and being re-fetched.
      await this.open()
      if (!this.db) throw new Error('AudioCache: DB not initialized')
      await this.db.put(STORE, row)
    } catch (err) {
      reportTrouble({ stage: 'put', where: 'ensure', name: (err as Error)?.name, message: (err as Error)?.message })
      throw err
    }

    if (opts.lifecycle === 'persistent') {
      this.persistentIds.add(id)
      // Defensive: if id was somehow in both Sets, ensure only persistent.
      this.ephemeralIds.delete(id)
    } else {
      this.ephemeralIds.add(id)
    }

    this.statsCache = null
  }

  private async persistentEvictToTarget(targetBytes: number): Promise<void> {
    await this.init()
    if (!this.db) return

    // Compute current persistent total.
    let total = 0
    {
      const tx = this.db.transaction(STORE, 'readonly')
      const idx = tx.objectStore(STORE).index('by-lifecycle')
      let cursor = await idx.openCursor('persistent')
      while (cursor) {
        total += cursor.value.size
        cursor = await cursor.continue()
      }
      await tx.done
    }
    if (total <= targetBytes) return

    // Walk by-last-accessed ascending; delete persistent rows oldest-first.
    const tx = this.db.transaction(STORE, 'readwrite')
    const idx = tx.objectStore(STORE).index('by-last-accessed')
    let cursor = await idx.openCursor()
    while (cursor && total > targetBytes) {
      const row = cursor.value
      if (row.lifecycle === 'persistent') {
        total -= row.size
        this.persistentIds.delete(row.id)
        // The bytes are gone — drop any cached WAV blob URL for this id too,
        // else a held blob: URL points at deleted data and plays silence/stalls.
        // Revoke it to release the blob.
        const staleWavUrl = this.wavUrlCache.get(row.id)
        if (staleWavUrl) { URL.revokeObjectURL(staleWavUrl); this.wavUrlCache.delete(row.id) }
        await cursor.delete()
      }
      cursor = await cursor.continue()
    }
    await tx.done

    this.statsCache = null
  }

  // ==========================================================================
  // EPHEMERAL
  // ==========================================================================

  private async ephemeralAcquireForLego(set: EphemeralLegoSet): Promise<void> {
    await this.init()

    // One AbortController per legoId — release aborts it.
    let ac = this.legoAborts.get(set.legoId)
    if (!ac) {
      ac = new AbortController()
      this.legoAborts.set(set.legoId, ac)
    }
    const signal = ac.signal

    const promises: Promise<void>[] = []
    for (const id of set.audioIds) {
      // Persistent wins — never duplicate.
      if (this.persistentIds.has(id)) continue
      if (this.ephemeralIds.has(id)) continue
      const existing = this.inflight.get(id)
      if (existing) {
        promises.push(existing)
        continue
      }
      const p = this.doFetchAndStore(id, {
        lifecycle: 'ephemeral',
        ephemeralOwnerLegoId: set.legoId,
        signal,
      })
        .catch((err) => {
          // Swallow AbortError — release-during-acquire is expected.
          if (err instanceof DOMException && err.name === 'AbortError') return
          throw err
        })
        .finally(() => {
          this.inflight.delete(id)
        })
      this.inflight.set(id, p)
      promises.push(p)
    }

    await Promise.all(promises)
  }

  private async ephemeralReleaseForLego(legoId: string): Promise<number> {
    await this.init()
    if (!this.db) return 0

    // Abort any in-flight acquires for this lego.
    const ac = this.legoAborts.get(legoId)
    if (ac) {
      ac.abort()
      this.legoAborts.delete(legoId)
    }

    // Walk by-ephemeral-owner and delete every matching row.
    let deleted = 0
    const tx = this.db.transaction(STORE, 'readwrite')
    const idx = tx.objectStore(STORE).index('by-ephemeral-owner')
    let cursor = await idx.openCursor(IDBKeyRange.only(legoId))
    while (cursor) {
      const row = cursor.value
      this.ephemeralIds.delete(row.id)
      await cursor.delete()
      deleted++
      cursor = await cursor.continue()
    }
    await tx.done

    this.statsCache = null
    return deleted
  }

  // ==========================================================================
  // READS
  // ==========================================================================

  has(id: AudioId): boolean {
    return this.persistentIds.has(id) || this.ephemeralIds.has(id)
  }

  async getBlobUrl(id: AudioId): Promise<string | null> {
    await this.init()
    if (!this.db) return null

    const row = await this.db.get(STORE, id)
    if (!row || this.isStaleRow(row)) return null

    // Fire-and-forget lastAccessedAt update.
    const updated: AudioRow = { ...row, lastAccessedAt: Date.now() }
    this.db.put(STORE, updated).catch((err) => {
      reportTrouble({ stage: 'put', where: 'touch', name: (err as Error)?.name, message: (err as Error)?.message })
    })

    // Guarantee the blob URL carries a decodable content-type. A blob with
    // an empty `.type` yields a typeless blob: URL that iOS Safari's <audio>
    // rejects with "operation is not supported" — the failure that got blob
    // playback dropped 2026-05-23. We captured the real type as `mimeType`
    // at fetch time; re-wrap only when the stored blob lost its own type.
    const typed = row.blob.type
      ? row.blob
      : new Blob([row.blob], { type: row.mimeType || 'audio/mpeg' })
    return URL.createObjectURL(typed)
  }

  /**
   * Offline playback URL: decode the cached (mp3) bytes and re-encode as a
   * WAV blob URL. WebKit's <audio> plays WAV blob URLs but NOT mp3 ones
   * ("operation is not supported"), so this is the offline-mode read path.
   * Decoded once per id and cached — spaced rep replays the same clips, so
   * we never decode the same audio twice. Null on miss / decode failure, so
   * the caller falls back to the network URL.
   */
  async getWavBlobUrl(id: AudioId): Promise<string | null> {
    const cached = this.wavUrlCache.get(id)
    if (cached) return cached
    // Coalesce concurrent decodes: one shared promise per id means one decode
    // and one object URL, so a racing caller can't leak a second URL.
    const existing = this.wavUrlInflight.get(id)
    if (existing) return existing
    const read = (async (): Promise<string | null> => {
      // Bytes a warm-up already holds need no IndexedDB read at all — which
      // on WebKit, where one origin's requests queue one behind another, is
      // what made the first clip miss its 250 ms bound and stream (job #298).
      const adopted = this.adoptedBytes.get(id)
      let bytes: ArrayBuffer
      if (adopted) {
        bytes = adopted.bytes
      } else {
        await this.init()
        if (!this.db) return null
        const row = await this.db.get(STORE, id)
        if (!row || this.isStaleRow(row)) return null
        bytes = await row.blob.arrayBuffer()
      }
      const wav = await bytesToWavBlob(bytes)
      if (!wav) return null
      const url = URL.createObjectURL(wav)
      // A timed-out read keeps running, so a second read for the same id can
      // land first. Keep the winner and revoke the loser rather than leaking a
      // blob URL nobody holds.
      const winner = this.wavUrlCache.get(id)
      if (winner) {
        URL.revokeObjectURL(url)
        return winner
      }
      // Cached even if the race below already timed out — a late decode still
      // benefits the next replay of this clip.
      this.wavUrlCache.set(id, url)
      return url
    })()
    // Hard ceiling (see WAV_READ_TIMEOUT_MS). Guarantees this memoised promise
    // ALWAYS settles, so a hung decode can never strand the playback path.
    let timer: ReturnType<typeof setTimeout> | undefined
    const bound = new Promise<string | null>((resolve) => {
      timer = setTimeout(() => {
        console.error(`[AudioCache] getWavBlobUrl(${id}) exceeded ${WAV_READ_TIMEOUT_MS}ms — falling back to the network URL`)
        resolve(null)
      }, WAV_READ_TIMEOUT_MS)
    })
    const work = Promise.race([read, bound]).finally(() => {
      if (timer) clearTimeout(timer)
      this.wavUrlInflight.delete(id)
    })
    this.wavUrlInflight.set(id, work)
    return work
  }

  /**
   * Take a clip's bytes from a warm-up that has already downloaded them (job
   * #298), and start its WAV decode now, so the clip's first hearing plays a
   * local blob instead of the network URL.
   *
   * Why it matters: the warm-ups used to throw the bytes away and count on
   * the browser's HTTP cache serving the <audio> element. Chromium does;
   * WebKit's media loader does not — measured on the #287 preview, the first
   * clip was fetched by the warm-up, fetched again by the element after
   * play() (probe + full GET, 190-450 ms of silence) and a third time by the
   * cache-ahead. Held here, one download serves all three. The signed-in
   * boot's first-window clips, fetched straight from S3 (jobs #299/#298),
   * come in the same way. Resolves true once the clip is playable from memory.
   *
   * `decode: false` holds the bytes without decoding them yet: twelve WAV
   * decodes at once, on a phone's main thread, landed on top of the first
   * sound (measured 4G + 4x CPU: ~550 ms from play() to sound). The held
   * clip decodes when it is first asked for, still with no fetch.
   */
  async adoptBytes(id: AudioId, bytes: ArrayBuffer, type = 'audio/mpeg', decode = true): Promise<boolean> {
    if (!bytes || bytes.byteLength === 0) return false
    this.adoptedBytes.delete(id)
    this.adoptedBytes.set(id, { bytes, type })
    while (this.adoptedBytes.size > ADOPTED_BYTES_MAX) {
      const oldest = this.adoptedBytes.keys().next().value
      if (oldest === undefined) break
      this.adoptedBytes.delete(oldest)
    }
    if (!decode) return true
    return !!(await this.getWavBlobUrl(id).catch(() => null))
  }

  hasPlayableUrl(id: AudioId): boolean {
    return this.wavUrlCache.has(id)
  }

  holdsClip(id: AudioId): boolean {
    return this.adoptedBytes.has(id) || this.wavUrlCache.has(id)
  }

  // ==========================================================================
  // QUOTA & STATS
  // ==========================================================================

  async quotaPressure(): Promise<number> {
    // Through the platform storage seam, so a native shell's real filesystem
    // quota can be swapped in without touching this call site.
    return storagePressure()
  }

  async stats(): Promise<AudioCacheStats> {
    const now = Date.now()
    if (this.statsCache && now - this.statsCache.at < STATS_TTL_MS) {
      return this.statsCache.value
    }
    await this.init()

    const result: AudioCacheStats = {
      persistent: { count: 0, bytes: 0 },
      ephemeral: { count: 0, bytes: 0 },
      quotaBytes: undefined,
      usageBytes: undefined,
    }

    if (this.db) {
      const tx = this.db.transaction(STORE, 'readonly')
      const idx = tx.objectStore(STORE).index('by-lifecycle')
      let cursor = await idx.openCursor()
      while (cursor) {
        const row = cursor.value
        const bucket = row.lifecycle === 'persistent' ? result.persistent : result.ephemeral
        bucket.count++
        bucket.bytes += row.size
        cursor = await cursor.continue()
      }
      await tx.done
    }

    // Best-effort quota/usage read, via the platform storage seam.
    const est = await estimateStorage()
    result.quotaBytes = est.quotaBytes
    result.usageBytes = est.usageBytes

    this.statsCache = { at: now, value: result }
    return result
  }

  /**
   * Close the underlying IndexedDB connection. Primarily for tests so
   * `indexedDB.deleteDatabase` isn't blocked by a held connection.
   * After close(), the next API call lazily re-opens.
   */
  close(): void {
    if (this.db) {
      this.db.close()
      this.db = null
      this.openPromise = null; this.hydratePromise = null
    }
    this.persistentIds.clear()
    this.ephemeralIds.clear()
    this.inflight.clear()
    this.adoptedBytes.clear()
    this.legoAborts.clear()
    // Revoke any outstanding WAV blob URLs so they don't leak across a close/
    // re-open (and so a stale URL can't survive the DB being recreated).
    for (const url of this.wavUrlCache.values()) URL.revokeObjectURL(url)
    this.wavUrlCache.clear()
    this.statsCache = null
  }
}
